import { DashboardState, ESdChangeReason } from "@kuika/kuika-cl-model";
import React from "react";
import { connect } from "react-redux";
import BranchingService from "../../../services/branching-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import { sortBranchesMasterFirst } from "../../../shared/utility/branch-helpers";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { SaveStatus } from "../../../shared/utility/save-status";
import { AppState } from "../../../store/root-reducer";
import { setNextCallbackFunction } from "../../../store/unsaved-changes-modal/actions";
import KuikaButton, {
  type IKuikaButtonStatusLabels,
  type KuikaButtonStatus
} from "../../ui-kit/kuika-button/kuika-button";
import KuikaDropdownMenu from "../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaSpinnerSmall from "../../ui-kit/kuika-spinner-small/kuika-spinner-small";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabs from "../../ui-kit/kuika-tabs/kuika-tabs";
import { toastService } from "../../ui-kit/kuika-toast/toast-service";
import KuikaH3 from "../../ui-kit/typography/H3/H3";
import KuikaH4 from "../../ui-kit/typography/H4/H4";
import KuikaSmallText from "../../ui-kit/typography/SmallText/SmallText";
import KuikaText from "../../ui-kit/typography/Text/Text";
import { BranchingPanel } from "../header/toolbar-left/branching-panel/branching-panel";
import { BranchMerge } from "./branch-merge";
import { isBranchingUIEnabled } from "./branching-feature-gate";
import "./branching-canvas.scss";
import BranchingMergeRequests from "./branching-merge-requests";
import BranchingRevertTimeline from "./branching-revert-timeline";
import BranchingStagedChanges from "./branching-staged-changes";
import CreateBranchModal from "./create-branch-modal";
import DeactivateBranchingModal from "./deactivate-branching-modal";

type BranchingWorkspaceTab = "changes" | "commits" | "merge" | "requests";

const ACTIVATE_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Activating", success: "Activated" };

export interface IBranchingCanvasState {
  isLoading: boolean;
  activeTab: BranchingWorkspaceTab;
  showDeactivateModal: boolean;
  pendingMergeRequestCount: number;
  showCreateBranchModal: boolean;
  newBranchName: string;
  createSourceBranchId: string;
  creatingBranch: boolean;
  /**
   * Where the page's own commits stand — create branch, deactivate, and the activate panel's
   * button; only one of them runs at a time. Owned by `SaveStatus`, never set directly.
   */
  commitStatus: KuikaButtonStatus;
}

export interface IBranchingCanvasProps {
  nextCallback?: () => void;
  setNextCallbackFunction: (cb?: () => void) => void;
}

class Branching extends PureSdComponent<IBranchingCanvasProps & IWithSDProps, IBranchingCanvasState> {
  private unsubscribeMergeIntent?: () => void;

  /**
   * The page's commit state machine. Create and activate hold their ✓, then what they changed
   * takes over (the new branch becomes current; the workspace replaces the activate panel);
   * deactivate says Deactivating and leaves its outcome to the toast.
   */
  private readonly commitStatus: SaveStatus = new SaveStatus((commitStatus: KuikaButtonStatus): void => {
    this.setState({ commitStatus });
  });

  private handleMergeIntent = () => {
    if (this.state.activeTab === "merge") {
      return;
    }
    this.setState({ activeTab: "merge" });
  };

  constructor(props: IBranchingCanvasProps & IWithSDProps) {
    super(props);

    this.state = {
      isLoading: false,
      activeTab: "changes",
      showDeactivateModal: false,
      pendingMergeRequestCount: 0,
      showCreateBranchModal: false,
      newBranchName: "",
      createSourceBranchId: "",
      creatingBranch: false,
      commitStatus: "idle"
    };
  }

  private openCreateBranch = () => {
    this.setState({
      showCreateBranchModal: true,
      newBranchName: "",
      createSourceBranchId: this.props.sd.currentBranch?.id ?? ""
    });
  };

  private handleCreateBranch = async () => {
    const { newBranchName, createSourceBranchId } = this.state;
    if (!this.commitStatus.canStart) return;
    this.commitStatus.start();
    this.setState({ creatingBranch: true });
    try {
      const created = await this.props.sd.createBranch({
        name: newBranchName.trim(),
        parentBranchId: createSourceBranchId || undefined
      });
      // The ✓ is held on CREATE; then the dialog closes and the new branch becomes current — the
      // header's branch chip and this page follow it.
      this.commitStatus.succeed((): void => {
        this.setState({ showCreateBranchModal: false, newBranchName: "" });
        if (created?.id) {
          void this.props.sd.setCurrentBranch(created.id);
        }
      });
    } catch {
      this.commitStatus.fail();
      KMainFunctions.displayErrorNotification("Failed to create branch.");
    } finally {
      this.setState({ creatingBranch: false });
    }
  };

  componentDidMount = () => {
    this.unsubscribeMergeIntent = BranchingService.SubscribeMergeIntent(this.handleMergeIntent);
    this.refreshMergeRequestCount();
  };

  private refreshMergeRequestCount = async () => {
    try {
      const res = await BranchingService.GetMergeRequests(true);
      this.setState({ pendingMergeRequestCount: res?.data?.length ?? 0 });
    } catch {
      // non-critical: the badge just won't show a count
    }
  };

  componentWillUnmount = () => {
    this.unsubscribeMergeIntent?.();
    this.commitStatus.dispose();
  };

  setLoading = (value: boolean) => {
    this.setState({ isLoading: value });
  };

  handleMergeToBranch = async () => {
    this.setState({ activeTab: "merge" });
  };

  // Merge to branch handlers
  handleCloseMergeToBranchModal = () => {
    this.setState({ activeTab: "changes" });
  };

  handleSelectTab = (tab: BranchingWorkspaceTab) => {
    this.setState({ activeTab: tab });
  };

  renderTabLabel = (icon: string, iconType: "common" | "topbar-icons", label: string) => (
    <span className="kuika-branching-canvas__tab-label">
      <KuikaSvg name={icon} type={iconType} width={14} height={14} />
      {label}
    </span>
  );

  /** Title + one-line description per workspace tab, shown at the top of the card's content pane. */
  private static readonly TAB_HEADINGS: Record<BranchingWorkspaceTab, { title: string; description: string }> = {
    changes: {
      title: "Changes",
      description: "Uncommitted work on this branch — review, commit, or discard it."
    },
    commits: {
      title: "Commits",
      description: "This branch's commit history — inspect or restore any point."
    },
    merge: {
      title: "Merge",
      description: "Merge this branch into another one, resolving conflicts along the way."
    },
    requests: {
      title: "Requests",
      description: "Merge requests waiting on protected branches you can approve."
    }
  };

  handleDeactivateBranching = () => {
    this.setState({ showDeactivateModal: true });
  };

  handleCloseDeactivateModal = () => {
    if (this.state.isLoading || !this.commitStatus.canStart) return;
    this.setState({ showDeactivateModal: false });
  };

  handleConfirmDeactivateBranching = async () => {
    if (!this.commitStatus.canStart) return;
    this.commitStatus.start();
    this.setState({ isLoading: true });
    try {
      await this.props.sd.deactivateBranching();
      this.props.sd.loadScreen(this.props.sd.sdId, true, true);
      this.props.sd.setDashboardState(DashboardState.design);
      // The branching UI is gone with it: the toast says so, not a ✓.
      this.commitStatus.reset();
      KMainFunctions.displaySuccessNotification("Version Control has been deactivated.");
      this.setState({ showDeactivateModal: false });
    } catch (error) {
      this.commitStatus.fail();
      KMainFunctions.displayErrorNotification("Failed to deactivate Version Control. Please try again.");
      console.error("Error deactivating Version Control:", error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleActivateBranching = () => {
    console.log("Activate Branching");
    const { currentBranch } = this.props.sd;
    const { isLoading } = this.state;
    if (currentBranch || isLoading || !this.commitStatus.canStart || !isBranchingUIEnabled()) {
      return;
    }
    this.commitStatus.start();
    BranchingService.InitializeBranching()
      .then((res) => {
        if (res.status === 200 && res.data) {
          console.log("Branching initialized:", res.data);
          const masterBranchId: string = res.data.id;
          // The ✓ is held on the button; adopting the branch then swaps this panel for the
          // workspace, which would take the button — and its confirmation — with it.
          this.commitStatus.succeed((): void => {
            this.props.sd.setCurrentBranch(masterBranchId);
            this.props.sd.loadScreen(this.props.sd.sdId, true, true);
          });
        } else {
          this.commitStatus.fail();
        }
      })
      .catch((error) => {
        this.commitStatus.fail();
        KMainFunctions.displayErrorNotification("Failed to activate Version Control. Please try again.");
        console.error("Error activating Version Control:", error);
      });
  };

  renderActivateBranching = () => {
    const { currentBranch } = this.props.sd;
    const { isLoading } = this.state;

    if (currentBranch) {
      return null;
    }

    if (!isBranchingUIEnabled()) {
      return (
        <div className="kuika-branching-canvas__activate">
          <div className="kuika-branching-canvas__activate-icon kuika-branching-canvas__activate-icon--muted">
            <KuikaSvg name="branching" type="topbar-icons" width={28} height={28} />
          </div>
          <KuikaH3 className="kuika-branching-canvas__activate-title">Version Control isn't available</KuikaH3>
          <KuikaText className="kuika-branching-canvas__activate-desc">
            This feature isn't enabled for your workspace yet. Contact your administrator to turn it on.
          </KuikaText>
        </div>
      );
    }

    return (
      <div className="kuika-branching-canvas__activate">
        <div className="kuika-branching-canvas__activate-icon">
          <KuikaSvg name="branching" type="topbar-icons" width={28} height={28} />
        </div>
        <KuikaH3 className="kuika-branching-canvas__activate-title">Version Control isn't active yet</KuikaH3>
        <KuikaText className="kuika-branching-canvas__activate-desc">
          Track every change, branch off to experiment safely, and merge back when you're ready — all without touching
          your master design until you choose to.
        </KuikaText>
        <div className="kuika-branching-canvas__activate-hints">
          <span className="kuika-branching-canvas__activate-hint">
            <KuikaSvg name="branching" type="topbar-icons" width={14} height={14} />
            Branch
          </span>
          <span className="kuika-branching-canvas__activate-hint">
            <KuikaSvg name="git-commit" width={14} height={14} />
            Commit
          </span>
          <span className="kuika-branching-canvas__activate-hint">
            <KuikaSvg name="git-merge" width={14} height={14} />
            Merge
          </span>
        </div>
        {/* Its own status rather than the page spinner: the button says Activating, then holds
            its ✓ before the workspace replaces this panel. */}
        <KuikaButton
          type="primary"
          size="md"
          status={this.state.commitStatus}
          statusLabels={ACTIVATE_STATUS_LABELS}
          disabled={isLoading}
          onClick={() => this.handleActivateBranching()}
        >
          Activate Version Control
        </KuikaButton>
      </div>
    );
  };

  handleEndMerge = async (options?: { completed?: boolean; targetBranchId?: string }) => {
    if (options?.completed && options.targetBranchId) {
      const targetBranchId: string = options.targetBranchId;
      const targetName: string = this.props.sd.branches?.find((b) => b.id === targetBranchId)?.name ?? "target";
      // UpdateUserBranchingState (backend) and setCurrentBranch (frontend reload) are independent.
      // setCurrentBranch → openProject(true) already fetches branches, so no separate getBranches needed.
      // The merge's own toast has said it completed; this one carries the switch that follows it,
      // from "Switching" to its outcome, rather than announce the merge a second time.
      const work: Promise<void> = Promise.all([
        BranchingService.UpdateUserBranchingState(targetBranchId),
        this.props.sd.setCurrentBranch(targetBranchId)
      ]).then((): void => undefined);
      toastService.promise(work, {
        loading: `Switching to ${targetName}…`,
        success: `Switched to branch: ${targetName}.`,
        error: `Could not switch to ${targetName}.`
      });
      await work;
    }
    // No reload for non-completed (cancel) path — screen data hasn't changed.

    this.setState({ activeTab: options?.completed ? "commits" : "changes" });
    // Only leave the branching workspace for the designer after a real merge.
    // Cancelling/closing the merge modal (incl. the X icon) must stay in branching
    // on the Changes tab rather than navigating away to the designer.
    if (options?.completed) {
      this.props.sd.setDashboardState(DashboardState.design);
    }
  };

  render() {
    const { isLoading, activeTab } = this.state;
    const { currentBranch } = this.props.sd;
    const isMergeInProgress = activeTab === "merge";
    const heading = Branching.TAB_HEADINGS[activeTab];

    return (
      <div className="kuika-branching-canvas">
        <KuikaSpinnerSmall spinning={isLoading}>
          {currentBranch && (
            <div className="kuika-branching-canvas__topbar">
              <div className="kuika-branching-canvas__switcher">
                <KuikaTabs
                  size="md"
                  value={activeTab}
                  onChange={(value) => this.handleSelectTab(value as BranchingWorkspaceTab)}
                  options={[
                    {
                      value: "changes",
                      dataTestId: "branchingTabChanges",
                      label: this.renderTabLabel("branching", "topbar-icons", "Changes")
                    },
                    {
                      value: "commits",
                      dataTestId: "branchingTabCommits",
                      label: this.renderTabLabel("git-commit", "common", "Commits")
                    },
                    {
                      value: "merge",
                      dataTestId: "branchingTabMerge",
                      label: this.renderTabLabel("git-merge", "common", "Merge")
                    },
                    {
                      value: "requests",
                      dataTestId: "branchingTabRequests",
                      label: this.renderTabLabel(
                        "git-merge",
                        "common",
                        this.state.pendingMergeRequestCount > 0
                          ? `Requests (${this.state.pendingMergeRequestCount})`
                          : "Requests"
                      )
                    }
                  ]}
                />
              </div>
              {!isMergeInProgress && (
                // Shrink-wrapped: the dropdown primitive's trigger wrapper is width:100%, which as
                // a bare flex child would swallow the topbar's free space (displacing the cog AND
                // inflating the overlay, whose min-width follows the trigger).
                <div className="kuika-branching-canvas__topbar-actions">
                  <KuikaDropdownMenu
                    options={[
                      {
                        key: "merge",
                        label: "Merge to branch",
                        icon: <KuikaSvg name="git-merge" width={16} height={16} />,
                        onClick: () => this.handleMergeToBranch(),
                        group: 1
                      },
                      {
                        key: "deactivate",
                        label: "Deactivate Version Control",
                        icon: <KuikaSvg name="circle-x" width={16} height={16} />,
                        onClick: () => this.handleDeactivateBranching(),
                        danger: true,
                        group: 2,
                        // Mirrors the server rule (ProjectOwner-only); undefined = fail open.
                        hidden: currentBranch?.permissions?.canDeactivate === false
                      }
                    ]}
                    trigger="click"
                    placement="bottomRight"
                  >
                    {(open) => (
                      <KuikaButton type="secondary" size="sm" isIcon active={open}>
                        <KuikaSvg name="settings" type="sidebar-icons" width={16} height={16} />
                      </KuikaButton>
                    )}
                  </KuikaDropdownMenu>
                </div>
              )}
            </div>
          )}
          {currentBranch ? (
            <div className="kuika-branching-canvas__card">
              <aside className="kuika-branching-canvas__sidebar">
                <BranchingPanel />
              </aside>
              <section className="kuika-branching-canvas__main">
                <div className="kuika-branching-canvas__main-head">
                  <KuikaH4 className="kuika-branching-canvas__main-title">{heading.title}</KuikaH4>
                  <KuikaText className="kuika-branching-canvas__main-desc">{heading.description}</KuikaText>
                  <div className="kuika-branching-canvas__identity">
                    <KuikaSvg name="branching" type="topbar-icons" width={16} height={16} />
                    <KuikaSmallText className="kuika-branching-canvas__identity-eyebrow">
                      Version Control
                    </KuikaSmallText>
                    <span className="kuika-branching-canvas__identity-name">{currentBranch.name}</span>
                    <span className="kuika-branching-canvas__identity-status" aria-label="Active branch" />
                  </div>
                </div>
                {currentBranch.isProtected && currentBranch.permissions?.canMergeInto === false && (
                  <div className="kuika-branching-canvas__protected-note">
                    <KuikaSvg name="lock" width={15} height={15} />
                    <span>
                      You’re on <strong>{currentBranch.name}</strong>, a protected branch — you can view it but can’t
                      commit here. Create your own branch to make changes, then merge into it.
                    </span>
                    {currentBranch.permissions?.canCreateBranch !== false && (
                      <KuikaButton type="primary" size="sm" onClick={this.openCreateBranch}>
                        Create branch
                      </KuikaButton>
                    )}
                  </div>
                )}
                <div className="kuika-branching-canvas__main-body">
                  {activeTab === "merge" &&
                    React.createElement(BranchMerge as any, {
                      isLoading,
                      setLoading: this.setLoading,
                      endMerge: this.handleEndMerge
                    })}
                  {activeTab === "changes" && (
                    <BranchingStagedChanges
                      branchId={currentBranch.id}
                      onAfterDiscard={() => this.props.sd.loadScreen(this.props.sd.sdId, true, true)}
                    />
                  )}
                  {activeTab === "commits" && (
                    <BranchingRevertTimeline
                      branchId={currentBranch.id}
                      setLoading={this.setLoading}
                      onAfterRevert={() => this.props.sd.loadScreen(this.props.sd.sdId, true, true)}
                    />
                  )}
                  {activeTab === "requests" && (
                    <BranchingMergeRequests
                      onCountChange={(count) => this.setState({ pendingMergeRequestCount: count })}
                      onAfterApprove={() => this.props.sd.loadScreen(this.props.sd.sdId, true, true)}
                    />
                  )}
                </div>
              </section>
            </div>
          ) : (
            <div className="kuika-branching-canvas__content">{this.renderActivateBranching()}</div>
          )}
        </KuikaSpinnerSmall>
        <DeactivateBranchingModal
          showModal={this.state.showDeactivateModal}
          inProgress={isLoading}
          status={this.state.commitStatus}
          onClose={this.handleCloseDeactivateModal}
          onDeactivate={this.handleConfirmDeactivateBranching}
        />
        <CreateBranchModal
          showModal={this.state.showCreateBranchModal}
          branchName={this.state.newBranchName}
          selectedSourceBranch={this.state.createSourceBranchId}
          branchOptions={sortBranchesMasterFirst(this.props.sd.branches).map((b) => ({ value: b.id, label: b.name }))}
          existingBranchNames={(this.props.sd.branches ?? []).map((b) => b.name ?? "")}
          inProgress={this.state.creatingBranch}
          status={this.state.commitStatus}
          onClose={() => this.commitStatus.canStart && this.setState({ showCreateBranchModal: false })}
          onBranchNameChange={(e) => this.setState({ newBranchName: e.target.value })}
          onSourceBranchChange={(value) =>
            this.setState({ createSourceBranchId: typeof value === "string" ? value : "" })
          }
          onCreateBranch={this.handleCreateBranch}
        />
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const mapStateToProps = (state: AppState) => ({
  nextCallback: state.unsavedChangesModal.nextCallback
});

const mapDispatchToProps = (dispatch: any) => ({
  setNextCallbackFunction: (cb?: () => void) => dispatch(setNextCallbackFunction(cb))
});

const branchingCanvas = connect(mapStateToProps, mapDispatchToProps)(withSD<IBranchingCanvasProps>(Branching));
export { branchingCanvas as BranchingCanvas };
